import React, { useState, useEffect, useCallback } from 'react';
import ApiBaseUrl from '../constants/apiUrl';
import { useNavigate } from 'react-router-dom';

const InstallmentApplications = () => {
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
    const [sortOrder, setSortOrder] = useState('latest'); // 'latest' | 'oldest'
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(12);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    
    const navigate = useNavigate();

    const fetchApplications = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth'));
            const url = new URL(`${ApiBaseUrl}/getAllApplications`);
            url.searchParams.append('page', String(page));
            url.searchParams.append('limit', String(limit));
            if (statusFilter !== 'all') {
                url.searchParams.append('status', statusFilter);
            }

            const res = await fetch(url, {
                headers: {
                    ...(authData?.token ? { Authorization: `Bearer ${authData.token}` } : {}),
                }
            });
            const data = await res.json();
            if (data.success) {
                setApplications(data.data || []);
                const total = data.total || (data.data ? data.data.length : 0);
                setTotalCount(total);
                setTotalPages(data.totalPages || Math.max(1, Math.ceil(total / limit)));
            } else {
                setError(data.message || "Failed to fetch applications.");
                setApplications([]);
            }
        } catch (err) {
            setError("Failed to fetch applications.");
            setApplications([]);
        } finally {
            setLoading(false);
        }
    }, [statusFilter, page, limit]);

    useEffect(() => {
        fetchApplications();
    }, [fetchApplications]);

    const handleStatusFilterChange = (status) => {
        setStatusFilter(status);
        setPage(1);
    };

    const handleDelete = async (appId) => {
        if (!appId) return;
        if (!window.confirm("Are you sure you want to delete this application?")) return;
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth'));
            const res = await fetch(`${ApiBaseUrl}/deleteInstallmentApplication/${appId}`, {
                method: 'DELETE',
                headers: {
                    ...(authData?.token ? { Authorization: `Bearer ${authData.token}` } : {})
                }
            });
            const data = await res.json();
            if (data.success) {
                fetchApplications();
            } else {
                alert(data.message || "Failed to delete application");
            }
        } catch (err) {
            alert("Failed to delete application.");
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'approved': return 'bg-emerald-50 text-emerald-600 border-emerald-100';
            case 'pending': return 'bg-orange-50 text-orange-600 border-orange-100';
            case 'rejected': return 'bg-red-50 text-red-600 border-red-100';
            case 'in_progress': return 'bg-blue-50 text-blue-600 border-blue-100';
            case 'completed': return 'bg-purple-50 text-purple-600 border-purple-100';
            case 'cancelled': return 'bg-gray-100 text-gray-500 border-gray-200';
            default: return 'bg-gray-50 text-gray-600 border-gray-100';
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return 'N/A';
        return date.toLocaleDateString('en-PK', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const filteredApplications = applications.filter(app => {
        const user = app.UserInfo?.[0] || {};
        const plan = app.PlanInfo?.[0] || {};
        const fin = app.PlanInfo?.[1] || {};
        const term = searchTerm.toLowerCase().trim();
        if (!term) return true;

        const matchesSearch =
            (user.name?.toLowerCase().includes(term)) ||
            (user.phone?.includes(term)) ||
            (user.cnic?.includes(term)) ||
            (plan.productName?.toLowerCase().includes(term)) ||
            (plan.planType?.toLowerCase().includes(term)) ||
            (fin.bankName?.toLowerCase().includes(term)) ||
            (app.applicationId?.toLowerCase().includes(term)) ||
            (app._id?.toLowerCase().includes(term));
        return matchesSearch;
    });

    const sortedApplications = [...filteredApplications].sort((a, b) => {
        const dateA = new Date(a.createdAt || a.appliedAt || a.date || 0).getTime();
        const dateB = new Date(b.createdAt || b.appliedAt || b.date || 0).getTime();
        return sortOrder === 'latest' ? dateB - dateA : dateA - dateB;
    });

    if (loading && applications.length === 0) return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4">
            <div className="w-12 h-12 border-4 border-gray-100 border-t-red-600 rounded-full animate-spin"></div>
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.3em]">Accessing Application Vault...</p>
        </div>
    );

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            {/* Header Section */}
            <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-gray-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                <div>
                    <h1 className="text-3xl font-black text-gray-900 tracking-tighter uppercase">Installment Applications</h1>
                    <p className="text-[10px] font-black text-red-600 uppercase tracking-[0.3em] mt-1">Reviewing Consumer Credit Requests ({totalCount} Total)</p>
                </div>
                <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0 scrollbar-hide w-full md:w-auto">
                    {['all', 'pending', 'in_progress', 'approved', 'rejected', 'completed'].map(status => (
                        <button
                            key={status}
                            onClick={() => handleStatusFilterChange(status)}
                            className={`px-5 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest whitespace-nowrap transition-all ${statusFilter === status ? 'bg-gray-900 text-white shadow-xl shadow-gray-200' : 'bg-gray-50 text-gray-400 hover:bg-gray-100'}`}
                        >
                            {status.replace('_', ' ')}
                        </button>
                    ))}
                </div>
            </div>

            {/* Controls Bar: Search, Sort, Limit & View Switcher */}
            <div className="flex flex-col lg:flex-row gap-4 justify-between items-stretch lg:items-center">
                {/* Search Bar */}
                <div className="relative flex-1">
                    <input
                        type="text"
                        placeholder="SEARCH BY APPLICANT NAME, PHONE, CNIC OR APP ID..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-14 pr-6 py-4 bg-white border border-gray-100 rounded-[2rem] text-[11px] font-black uppercase tracking-widest outline-none transition-all shadow-sm focus:ring-4 focus:ring-red-50/50 focus:border-red-600"
                    />
                    <svg className="absolute left-6 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                </div>

                {/* Filters, Limits & View Mode Switcher */}
                <div className="flex items-center gap-3 flex-wrap">
                    {/* Sort Order Dropdown */}
                    <div className="flex items-center bg-white border border-gray-100 rounded-2xl px-4 py-3 shadow-sm">
                        <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest mr-2">Sort:</span>
                        <select
                            value={sortOrder}
                            onChange={(e) => setSortOrder(e.target.value)}
                            className="text-[10px] font-black text-gray-900 uppercase bg-transparent outline-none cursor-pointer"
                        >
                            <option value="latest">Latest First</option>
                            <option value="oldest">Oldest First</option>
                        </select>
                    </div>

                    {/* Per Page Limit Dropdown */}
                    <div className="flex items-center bg-white border border-gray-100 rounded-2xl px-4 py-3 shadow-sm">
                        <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest mr-2">Show:</span>
                        <select
                            value={limit}
                            onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                            className="text-[10px] font-black text-gray-900 uppercase bg-transparent outline-none cursor-pointer"
                        >
                            <option value={9}>9 / Page</option>
                            <option value={12}>12 / Page</option>
                            <option value={24}>24 / Page</option>
                            <option value={48}>48 / Page</option>
                        </select>
                    </div>

                    {/* View Mode Toggle Buttons */}
                    <div className="flex bg-gray-100 p-1.5 rounded-2xl border border-gray-200/50">
                        <button
                            onClick={() => setViewMode('grid')}
                            title="Grid View"
                            className={`p-2.5 rounded-xl transition-all ${viewMode === 'grid' ? 'bg-white text-red-600 shadow-md font-bold' : 'text-gray-400 hover:text-gray-700'}`}
                        >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                            </svg>
                        </button>
                        <button
                            onClick={() => setViewMode('list')}
                            title="List / Form View"
                            className={`p-2.5 rounded-xl transition-all ${viewMode === 'list' ? 'bg-white text-red-600 shadow-md font-bold' : 'text-gray-400 hover:text-gray-700'}`}
                        >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            {error && <div className="p-4 bg-red-50 border-2 border-red-100 text-red-600 rounded-2xl font-bold uppercase text-[10px] tracking-widest">{error}</div>}

            {/* Applications Render: Grid View vs List View */}
            {viewMode === 'grid' ? (
                /* Applications Grid View */
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {sortedApplications.map((app) => {
                        const user = app.UserInfo?.[0] || {};
                        const plan = app.PlanInfo?.[0] || {};
                        const fin = app.PlanInfo?.[1] || {};
                        const targetId = app.applicationId || app._id;
                        const appDate = formatDate(app.createdAt || app.appliedAt || app.date);

                        return (
                            <div key={app._id} className="bg-white rounded-[2.5rem] border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-500 overflow-hidden flex flex-col group">
                                <div className="p-8 flex-1 space-y-6">
                                    <div className="flex justify-between items-start">
                                        <div className={`px-3 py-1.5 rounded-lg text-[8px] font-black uppercase tracking-widest border ${getStatusColor(app.status)}`}>
                                            {app.status?.replace('_', ' ') || 'pending'}
                                        </div>
                                        <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest bg-gray-50 px-2.5 py-1 rounded-md">
                                            #{targetId || 'NO_ID'}
                                        </span>
                                    </div>

                                    <div className="space-y-4">
                                        <div>
                                            <h3 className="text-lg font-black text-gray-900 tracking-tight uppercase line-clamp-1">
                                                {user.name || 'Anonymous Applicant'}
                                            </h3>
                                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">
                                                {user.city || 'Location N/A'} • {user.phone || 'N/A'}
                                            </p>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-50">
                                            <div>
                                                <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest">Product / Plan</p>
                                                <p className="text-xs font-black text-gray-900 line-clamp-1">{plan.productName || plan.planType || 'Standard Plan'}</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest">Bank</p>
                                                <p className="text-xs font-black text-gray-900 line-clamp-1">{fin.bankName || 'Alkhidmat Islamic'}</p>
                                            </div>
                                        </div>

                                        <div className="flex justify-between items-center text-[9px] font-black uppercase tracking-widest text-gray-400 pt-1">
                                            <span>Applied Date</span>
                                            <span className="text-gray-900 bg-gray-100 px-2 py-0.5 rounded">{appDate}</span>
                                        </div>

                                        <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100 space-y-2">
                                            <div className="flex justify-between items-center text-[9px] font-black uppercase tracking-widest text-gray-400">
                                                <span>Monthly Burden</span>
                                                <span className="text-red-600">RS. {fin.monthlyInstallment?.toLocaleString() || '0'}</span>
                                            </div>
                                            <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                                <div className="h-full bg-red-600 rounded-full" style={{ width: '45%' }}></div>
                                            </div>
                                            <div className="flex justify-between text-[8px] font-bold text-gray-400 uppercase">
                                                <span>Tenure: {fin.tenureMonths || '0'} Months</span>
                                                <span>Downpayment: RS. {fin.downPayment?.toLocaleString() || '0'}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="px-8 py-6 bg-gray-50/50 border-t border-gray-50 flex gap-2">
                                    <button
                                        onClick={() => navigate(`/installments/application/${targetId}`)}
                                        className="flex-1 py-3 bg-gray-900 text-white rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-black transition-all shadow-lg shadow-gray-100"
                                    >
                                        View Details
                                    </button>
                                    <button
                                        onClick={() => handleDelete(targetId)}
                                        title="Delete Application"
                                        className="px-4 py-3 bg-white text-gray-400 hover:text-red-600 rounded-xl transition-all border border-gray-100"
                                    >
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                /* Applications List / Form Table View */
                <div className="bg-white rounded-[2.5rem] border border-gray-100 shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="border-b border-gray-100 bg-gray-50/50 text-[9px] font-black text-gray-400 uppercase tracking-widest">
                                    <th className="py-5 px-6">App ID</th>
                                    <th className="py-5 px-6">Applicant Info</th>
                                    <th className="py-5 px-6">Product / Plan</th>
                                    <th className="py-5 px-6">Financials</th>
                                    <th className="py-5 px-6">Applied Date</th>
                                    <th className="py-5 px-6">Status</th>
                                    <th className="py-5 px-6 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50 text-[11px] font-bold">
                                {sortedApplications.map((app) => {
                                    const user = app.UserInfo?.[0] || {};
                                    const plan = app.PlanInfo?.[0] || {};
                                    const fin = app.PlanInfo?.[1] || {};
                                    const targetId = app.applicationId || app._id;
                                    const appDate = formatDate(app.createdAt || app.appliedAt || app.date);

                                    return (
                                        <tr key={app._id} className="hover:bg-gray-50/50 transition-colors">
                                            <td className="py-5 px-6 font-black text-gray-900 uppercase tracking-wider">
                                                <span className="bg-gray-100 px-2.5 py-1 rounded-lg text-[10px]">
                                                    #{targetId || 'N/A'}
                                                </span>
                                            </td>
                                            <td className="py-5 px-6">
                                                <div className="font-black text-gray-900 uppercase tracking-tight">{user.name || 'Anonymous Applicant'}</div>
                                                <div className="text-[10px] text-gray-400 uppercase tracking-widest mt-0.5">
                                                    {user.phone || 'N/A'} • {user.city || 'N/A'}
                                                </div>
                                            </td>
                                            <td className="py-5 px-6">
                                                <div className="font-black text-gray-900 uppercase">{plan.productName || plan.planType || 'Standard Plan'}</div>
                                                <div className="text-[10px] text-gray-400 uppercase tracking-widest">{fin.bankName || 'Partner Bank'}</div>
                                            </td>
                                            <td className="py-5 px-6">
                                                <div className="text-red-600 font-black">RS. {fin.monthlyInstallment?.toLocaleString() || '0'} / Mo</div>
                                                <div className="text-[9px] text-gray-400 uppercase font-semibold">
                                                    Tenure: {fin.tenureMonths || 0}M | Down: RS. {fin.downPayment?.toLocaleString() || 0}
                                                </div>
                                            </td>
                                            <td className="py-5 px-6 text-gray-500 font-black text-[10px] uppercase">
                                                {appDate}
                                            </td>
                                            <td className="py-5 px-6">
                                                <span className={`inline-block px-3 py-1 rounded-lg text-[8px] font-black uppercase tracking-widest border ${getStatusColor(app.status)}`}>
                                                    {app.status?.replace('_', ' ') || 'pending'}
                                                </span>
                                            </td>
                                            <td className="py-5 px-6 text-right">
                                                <div className="flex justify-end gap-2">
                                                    <button
                                                        onClick={() => navigate(`/installments/application/${targetId}`)}
                                                        className="px-4 py-2 bg-gray-900 text-white rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-black transition-all shadow-sm"
                                                    >
                                                        Details
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(targetId)}
                                                        title="Delete Application"
                                                        className="p-2 bg-white text-gray-400 hover:text-red-600 rounded-xl transition-all border border-gray-100"
                                                    >
                                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Empty State */}
            {sortedApplications.length === 0 && !loading && (
                <div className="flex flex-col items-center justify-center py-24 text-center bg-white rounded-[2.5rem] border border-gray-100">
                    <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                        <svg className="w-8 h-8 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                    </div>
                    <h3 className="text-base font-black text-gray-900 uppercase tracking-tighter">No Applications Found</h3>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">No installment applications match the selected filter or search term.</p>
                </div>
            )}

            {/* Pagination Controls */}
            {totalPages > 0 && sortedApplications.length > 0 && (
                <div className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm flex flex-col md:flex-row justify-between items-center gap-4">
                    <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                        Showing <span className="text-gray-900">{Math.min((page - 1) * limit + 1, totalCount)}</span> to <span className="text-gray-900">{Math.min(page * limit, totalCount)}</span> of <span className="text-red-600">{totalCount}</span> Applications
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            disabled={page === 1}
                            onClick={() => setPage(prev => Math.max(prev - 1, 1))}
                            className="px-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-[10px] font-black uppercase tracking-widest text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                        >
                            Previous
                        </button>

                        <div className="flex gap-1.5">
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                                <button
                                    key={pageNum}
                                    onClick={() => setPage(pageNum)}
                                    className={`w-9 h-9 rounded-xl text-[10px] font-black transition-all ${page === pageNum ? 'bg-red-600 text-white shadow-lg shadow-red-200' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`}
                                >
                                    {pageNum}
                                </button>
                            ))}
                        </div>

                        <button
                            disabled={page === totalPages || totalPages === 0}
                            onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
                            className="px-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-[10px] font-black uppercase tracking-widest text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InstallmentApplications;

